import { recordKey } from '../../collection/multiCollectionIdentity';
import {
  useCallback,
  useLayoutEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';
import { Box, List } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useWindowVirtualizer } from '@tanstack/react-virtual';
import { Item } from '../../../domain/item';
import {
  CARD_SIZE_WIDTHS,
  CardSize,
  DISPLAY_SIZE_PROFILES,
} from '../../../hooks/useUiPreferences';
import {
  CollectionSelectionController,
  CollectionView,
} from '../../collection/types';
import { ItemGridCard } from './ItemGridCard';
import { ItemListRow } from './ItemListRow';

export const ITEM_LIST_VIRTUAL_OVERSCAN = 6;
export const ITEM_LIST_CARD_GAP = 8;
export const ITEM_GRID_VIRTUAL_OVERSCAN = 3;

export function calculateGridColumnCount(
  containerWidth: number,
  minimumCardWidth: number,
  gap: number,
): number {
  if (!Number.isFinite(containerWidth) || containerWidth <= 0) return 0;
  if (!Number.isFinite(minimumCardWidth) || minimumCardWidth <= 0) return 1;
  const safeGap = Number.isFinite(gap) && gap >= 0 ? gap : 0;
  return Math.max(
    1,
    Math.floor((containerWidth + safeGap) / (minimumCardWidth + safeGap)),
  );
}

export function calculateGridRowCount(
  itemCount: number,
  columnCount: number,
): number {
  if (itemCount <= 0 || columnCount <= 0) return 0;
  return Math.ceil(itemCount / columnCount);
}

export function resolveMeasuredRowHeight(
  borderBoxHeight: number | undefined,
  rectHeight: number,
  offsetHeight: number,
  estimatedHeight: number,
): number {
  if (
    typeof borderBoxHeight === 'number' &&
    Number.isFinite(borderBoxHeight) &&
    borderBoxHeight > 0
  ) {
    return borderBoxHeight;
  }
  if (Number.isFinite(rectHeight) && rectHeight > 0) return rectHeight;
  if (Number.isFinite(offsetHeight) && offsetHeight > 0) return offsetHeight;
  return Number.isFinite(estimatedHeight) && estimatedHeight > 0
    ? estimatedHeight
    : 1;
}

function resolveSpacingPixels(spacing: string): number {
  const value = Number.parseFloat(spacing);
  if (!Number.isFinite(value)) return 0;
  if (spacing.trim().endsWith('rem') || spacing.trim().endsWith('em'))
    return value * 16;
  return value;
}

export function ItemResults({
  items,
  view,
  selection,
  onOpen,
  cardSize = 5,
  showLocation = true,
  showQuantity = true,
}: {
  items: Item[];
  view: CollectionView;
  selection: CollectionSelectionController;
  onOpen: (item: Item) => void;
  cardSize?: CardSize;
  showLocation?: boolean;
  showQuantity?: boolean;
}) {
  if (view === 'list') {
    return (
      <VirtualizedItemList
        items={items}
        selection={selection}
        onOpen={onOpen}
        cardSize={cardSize}
        showLocation={showLocation}
        showQuantity={showQuantity}
      />
    );
  }

  return (
    <VirtualizedItemGrid
      items={items}
      selection={selection}
      onOpen={onOpen}
      cardSize={cardSize}
      showLocation={showLocation}
      showQuantity={showQuantity}
    />
  );
}

function VirtualizedItemList({
  items,
  selection,
  onOpen,
  cardSize,
  showLocation = true,
  showQuantity = true,
}: {
  items: Item[];
  selection: CollectionSelectionController;
  onOpen: (item: Item) => void;
  cardSize: CardSize;
  showLocation?: boolean;
  showQuantity?: boolean;
}) {
  const size = DISPLAY_SIZE_PROFILES[cardSize];
  const listRef = useRef<HTMLDivElement>(null);
  const [scrollMargin, setScrollMargin] = useState(0);
  const [, refreshVirtualLayout] = useReducer((value: number) => value + 1, 0);
  const getItemKey = useCallback(
    (index: number) => (items[index] ? recordKey(items[index]) : index),
    [items],
  );
  const visibleIds = useMemo(() => items.map(recordKey), [items]);
  const virtualizer = useWindowVirtualizer<HTMLLIElement>({
    count: items.length,
    estimateSize: () => size.itemListRowHeight,
    gap: ITEM_LIST_CARD_GAP,
    getItemKey,
    overscan: ITEM_LIST_VIRTUAL_OVERSCAN,
    scrollMargin,
    useFlushSync: false,
  });

  const updateScrollMargin = useCallback(() => {
    const list = listRef.current;
    if (!list || typeof window === 'undefined') return;
    const nextScrollMargin = Math.max(
      0,
      Math.round(list.getBoundingClientRect().top + window.scrollY),
    );
    setScrollMargin((current) =>
      current === nextScrollMargin ? current : nextScrollMargin,
    );
  }, []);

  useLayoutEffect(() => {
    updateScrollMargin();
    const list = listRef.current;
    const parent = list?.parentElement;
    const resizeObserver =
      typeof ResizeObserver === 'undefined' || !parent
        ? null
        : new ResizeObserver(updateScrollMargin);
    if (resizeObserver && parent) resizeObserver.observe(parent);
    window.addEventListener('resize', updateScrollMargin);
    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener('resize', updateScrollMargin);
    };
  }, [updateScrollMargin]);

  useLayoutEffect(() => {
    virtualizer.measure();
    refreshVirtualLayout();
  }, [cardSize, virtualizer]);

  return (
    <Box ref={listRef} data-testid="inventory-list-container">
      <List
        disablePadding
        aria-label="Items"
        style={{ height: virtualizer.getTotalSize() }}
        sx={{
          position: 'relative',
          // Virtual rows are positioned explicitly; let their shadows paint
          // beyond the first/last row without changing measured geometry.
          overflow: 'visible',
        }}
      >
        {virtualizer.getVirtualItems().map((virtualRow) => {
          const item = items[virtualRow.index];
          if (!item) return null;
          return (
            <ItemListRow
              key={recordKey(item)}
              item={item}
              cardSize={cardSize}
              showLocation={showLocation}
              showQuantity={showQuantity}
              selection={selection}
              onOpen={onOpen}
              itemPosition={virtualRow.index + 1}
              totalItemCount={items.length}
              visibleIds={visibleIds}
              virtualStyle={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: virtualRow.size,
                transform: `translateY(${virtualRow.start - scrollMargin}px)`,
              }}
            />
          );
        })}
      </List>
    </Box>
  );
}

function VirtualizedItemGrid({
  items,
  selection,
  onOpen,
  cardSize,
  showLocation = true,
  showQuantity = true,
}: {
  items: Item[];
  selection: CollectionSelectionController;
  onOpen: (item: Item) => void;
  cardSize: CardSize;
  showLocation?: boolean;
  showQuantity?: boolean;
}) {
  const theme = useTheme();
  const size = DISPLAY_SIZE_PROFILES[cardSize];
  const gridRef = useRef<HTMLDivElement>(null);
  const [gridWidth, setGridWidth] = useState(0);
  const [scrollMargin, setScrollMargin] = useState(0);
  const [, refreshVirtualLayout] = useReducer((value: number) => value + 1, 0);
  const gridGapPx = resolveSpacingPixels(theme.spacing(size.gridGap));
  const visibleIds = useMemo(() => items.map(recordKey), [items]);

  const updateGridWidth = useCallback((width?: number) => {
    const grid = gridRef.current;
    if (!grid || typeof window === 'undefined') return;
    const measuredWidth =
      width ?? grid.clientWidth ?? grid.getBoundingClientRect().width;
    if (measuredWidth > 0) {
      const nextWidth = Math.round(measuredWidth);
      setGridWidth((current) => (current === nextWidth ? current : nextWidth));
      return;
    }
    if (typeof ResizeObserver === 'undefined') {
      const fallbackWidth = Math.round(
        window.innerWidth || document.documentElement.clientWidth || 0,
      );
      setGridWidth((current) =>
        current === fallbackWidth ? current : fallbackWidth,
      );
    }
  }, []);

  useLayoutEffect(() => {
    updateGridWidth();
    const grid = gridRef.current;
    if (!grid) return undefined;
    const resizeObserver =
      typeof ResizeObserver === 'undefined'
        ? null
        : new ResizeObserver(([entry]) =>
            updateGridWidth(entry?.contentRect.width),
          );
    resizeObserver?.observe(grid);
    const handleResize = () => updateGridWidth();
    window.addEventListener('resize', handleResize);
    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener('resize', handleResize);
    };
  }, [updateGridWidth]);

  const updateScrollMargin = useCallback(() => {
    const grid = gridRef.current;
    if (!grid || typeof window === 'undefined') return;
    const nextScrollMargin = Math.max(
      0,
      Math.round(grid.getBoundingClientRect().top + window.scrollY),
    );
    setScrollMargin((current) =>
      current === nextScrollMargin ? current : nextScrollMargin,
    );
  }, []);

  useLayoutEffect(() => {
    updateScrollMargin();
    const grid = gridRef.current;
    const parent = grid?.parentElement;
    const resizeObserver =
      typeof ResizeObserver === 'undefined' || !parent
        ? null
        : new ResizeObserver(updateScrollMargin);
    if (resizeObserver && parent) resizeObserver.observe(parent);
    window.addEventListener('resize', updateScrollMargin);
    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener('resize', updateScrollMargin);
    };
  }, [updateScrollMargin]);

  const columnCount = calculateGridColumnCount(
    gridWidth,
    CARD_SIZE_WIDTHS[cardSize],
    gridGapPx,
  );
  const rowCount = calculateGridRowCount(items.length, columnCount);
  // Rows are layout slots; item identity belongs to the cards inside them.
  // A new first item must not remount every image in the row.
  const getRowKey = useCallback((rowIndex: number) => rowIndex, []);
  const cardWidth =
    columnCount > 0
      ? (gridWidth - (columnCount - 1) * gridGapPx) / columnCount
      : 0;
  const estimatedRowHeight = Math.max(72, cardWidth + 104 + gridGapPx);
  const measureRow = useCallback(
    (element: HTMLDivElement, entry: ResizeObserverEntry | undefined) => {
      const borderBoxHeight = entry?.borderBoxSize?.[0]?.blockSize;
      const rectHeight = element.getBoundingClientRect().height;
      return resolveMeasuredRowHeight(
        borderBoxHeight,
        rectHeight,
        element.offsetHeight,
        estimatedRowHeight,
      );
    },
    [estimatedRowHeight],
  );
  const virtualizer = useWindowVirtualizer<HTMLDivElement>({
    count: rowCount,
    estimateSize: () => estimatedRowHeight,
    getItemKey: getRowKey,
    measureElement: measureRow,
    overscan: ITEM_GRID_VIRTUAL_OVERSCAN,
    scrollMargin,
    useFlushSync: false,
  });

  useLayoutEffect(() => {
    virtualizer.measure();
    // Clearing the cache after a width/density change also clears ref-time
    // measurements. Unchanged DOM sizes need an explicit measurement because
    // ResizeObserver will not necessarily deliver another size notification.
    gridRef.current
      ?.querySelectorAll<HTMLDivElement>('[data-index]')
      .forEach((row) => {
        virtualizer.resizeItem(
          Number(row.dataset.index),
          measureRow(row, undefined),
        );
      });
    refreshVirtualLayout();
  }, [cardSize, columnCount, gridWidth, measureRow, virtualizer]);

  return (
    <Box
      ref={gridRef}
      data-testid="inventory-grid"
      role="list"
      aria-label="Items"
      style={{ height: gridWidth > 0 ? virtualizer.getTotalSize() : 0 }}
      sx={{
        display: 'grid',
        position: 'relative',
        overflow: 'visible',
        width: '100%',
        alignItems: 'stretch',
        gridTemplateColumns: `repeat(auto-fill, minmax(min(100%, ${CARD_SIZE_WIDTHS[cardSize]}px), 1fr))`,
        gap: size.gridGap,
      }}
    >
      {virtualizer.getVirtualItems().map((virtualRow) => {
        const rowStart = virtualRow.index * columnCount;
        const rowItems = items.slice(rowStart, rowStart + columnCount);
        return (
          <Box
            key={`${columnCount}:${virtualRow.key}`}
            ref={virtualizer.measureElement}
            role="presentation"
            data-index={virtualRow.index}
            data-testid={`inventory-grid-row-${virtualRow.index}`}
            data-column-count={columnCount}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              transform: `translateY(${virtualRow.start - scrollMargin}px)`,
            }}
            sx={{
              display: 'grid',
              gridTemplateColumns: `repeat(${columnCount}, minmax(0, 1fr))`,
              columnGap: size.gridGap,
              paddingBottom: size.gridGap,
              boxSizing: 'border-box',
              alignItems: 'stretch',
            }}
          >
            {rowItems.map((item, itemOffset) => (
              <ItemGridCard
                key={recordKey(item)}
                item={item}
                showLocation={showLocation}
                showQuantity={showQuantity}
                selection={selection}
                onOpen={onOpen}
                itemPosition={rowStart + itemOffset + 1}
                totalItemCount={items.length}
                visibleIds={visibleIds}
              />
            ))}
          </Box>
        );
      })}
    </Box>
  );
}
